export const partnersEn = {
  discardTitle: 'Discard unsaved changes?',
  discardDescription:
    'Your changes have not been saved. Continue editing or explicitly discard them.',
  keepEditing: 'Continue editing',
  discard: 'Discard',

  all: {
    title: 'All contacts',
    subtitle: 'Complete overview of all business partners and their roles.',
  },
  customers: {
    title: 'Customers',
    subtitle: 'Accounts receivable, terms and revenue of all active customers.',
  },
  prospects: {
    title: 'Prospects / leads',
    subtitle: 'Sales pipeline from first contact to closing.',
  },
  suppliers: {
    title: 'Suppliers',
    subtitle: 'Accounts payable, ratings and purchasing conditions.',
  },
  searchPlaceholder: 'Search name, number, city, VAT ID, account …',
  searchLabel: 'Search business partners',
  columnsMenu: 'Columns',
  createTitle: 'New business partner',
  editTitle: 'Edit business partner',
  formDescription:
    'The roles control which fields appear. The partner number is assigned automatically if left empty.',
  created: 'Business partner created.',
  updated: 'Business partner saved.',
  saveError: 'The business partner could not be saved.',
  validationTitle: 'Check the details',
  validationSummary: 'Please check the highlighted details. Your entries have been retained.',
  quick: {
    title: 'Quick create',
    description:
      'A role and name are enough to create the record. Everything else can be added later.',
    completeTitle: 'Complete creation',
    completeDescription: 'Edit all available master data and terms.',
    showAll: 'Show all fields',
    showQuick: 'Back to quick create',
    defaultsUnavailable:
      'Tenant defaults are temporarily unavailable. Safe default values are shown.',
    step1: '1. Customer or company',
    step2: '2. Address (optional)',
    step3: '3. Contact (optional)',
    step4: '4. Billing and tax (optional)',
    completionHint:
      'You can complete the record after saving. Before posting, the document lists missing business details in a checklist.',
  },
  roles: {
    customer: 'Customer',
    prospect: 'Prospect',
    supplier: 'Supplier',
  },
  actions: {
    create: 'New partner',
    createCustomer: 'New customer',
    createProspect: 'New prospect',
    createSupplier: 'New supplier',
    export: 'Export',
    call: 'Call',
    email: 'Email',
    logCall: 'Log call',
    createQuote: 'Create document',
    convert: 'Convert to customer',
    backToList: 'Back to overview',
  },
  tabs: {
    master: 'Master data & addresses',
    sales: 'Revenue & documents',
    purchasing: 'Purchasing & items',
    orders: 'Purchase orders',
    insurance: 'Credit limit & insurance',
    customFields: 'Custom fields',
    history: 'History',
    questionnaires: 'Questionnaires',
    telephony: 'Telephony & history',
    whatsapp: 'WhatsApp',
  },
  sections: {
    roles: 'Roles',
    rolesHint:
      'A partner can be customer, prospect and supplier at the same time. At least one role is required.',
    masterData: 'Master data',
    address: 'Address',
    tax: 'Tax & identification',
    customerConditions: 'Customer terms',
    prospect: 'Prospect & pipeline',
    supplier: 'Supplier data',
    customFields: 'Custom fields',
    customFieldsHint:
      'Fields with a group prefix (customer:, prospect:, supplier:) only appear for the matching role.',
  },
  detail: {
    since: 'Created on {{date}}',
  },
  values: {
    days_one: '{{count}} day',
    days_other: '{{count}} days',
  },
  columns: {
    partnerNumber: 'Number',
    displayName: 'Name',
    roles: 'Roles',
    status: 'Status',
    industry: 'Industry',
    industryCode: 'Industry code',
    email: 'Email',
    phone: 'Phone',
    city: 'City',
    countryCode: 'Country',
    tags: 'Tags',
    debtorAccountNumber: 'Debtor',
    paymentTermsDays: 'Payment terms',
    creditLimit: 'Credit limit',
    leadStatus: 'Stage',
    leadSource: 'Source',
    leadScore: 'Score',
    dealSizeEstimated: 'Potential',
    winProbability: 'Probability',
    expectedClosingDate: 'Expected close',
    creditorAccountNumber: 'Creditor',
    supplierRating: 'Rating',
    minimumOrderValue: 'Minimum order value',
    deliveryTimeDays: 'Lead time',
    riskStatus: 'Risk',
  },
  fields: {
    type: 'Partner type',
    status: 'Status',
    partnerNumber: 'Partner number',
    partnerNumberHint: 'Leave empty for automatic assignment (KD-, INT-, LF-).',
    industry: 'Industry',
    industryCode: 'Industry code',
    industryCodeHint:
      'Optional: WZ 2025 for Germany, NAF/APE for France. Search by code or official name; selecting a code automatically fills the industry.',
    industryCodeSearchHint:
      '{{count}} codes available. Enter a code or search term to narrow the selection.',
    industryCodeInvalid: 'Enter text with no more than {{max}} characters.',
    companyName: 'Company name',
    tradingName: 'Trading name',
    legalForm: 'Legal form',
    department: 'Department',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    phone: 'Phone',
    mobile: 'Mobile',
    website: 'Website',
    street: 'Street',
    houseNumber: 'House number',
    addressLine2: 'Address line 2',
    postalCode: 'Postal code',
    city: 'City',
    region: 'Region',
    countryCode: 'Country',
    vatId: 'VAT ID',
    vatIdStatus: 'VAT ID status',
    taxNumber: 'Tax number',
    siren: 'SIREN',
    siret: 'SIRET',
    taxCategory: 'Sales tax strategy',
    gln: 'GLN',
    eoriNumber: 'EORI number',
    preferredLanguage: 'Preferred document language',
    preferredLanguageHint: 'Used for new documents, portal content, and customer-related emails.',
    currency: 'Currency',
    ownerName: 'Account manager',
    tags: 'Tags',
    tagsHint: 'Separate multiple values with commas.',
    debtorAccountNumber: 'DATEV debtor account',
    debtorAccountHint: 'Valid range: 10000–69999.',
    creditLimit: 'Credit limit',
    creditRating: 'Credit rating',
    creditRatingHint: 'Creditreform scale 100–600; lower is better.',
    priceGroup: 'Price group',
    defaultDiscountPct: 'Discount',
    paymentTerms: 'Payment terms',
    paymentTermsDays: 'Payment terms (days)',
    discountPercent: 'Cash discount',
    discountDays: 'Cash discount period (days)',
    deliveryTerms: 'Delivery terms',
    minRemainingShelfLifeDays: 'Minimum remaining shelf life (days)',
    deliveryBlock: 'Delivery block',
    dunningBlock: 'Dunning block',
    leadStatus: 'Pipeline stage',
    leadSource: 'Source',
    leadScore: 'Lead score (0–100)',
    dealSizeEstimated: 'Estimated deal size',
    winProbability: 'Win probability (%)',
    expectedClosingDate: 'Expected closing date',
    lostReason: 'Loss reason',
    convertedAt: 'Converted on',
    creditorAccountNumber: 'DATEV creditor account',
    creditorAccountHint: 'Valid range: 70000–99999.',
    supplierRating: 'Supplier rating',
    minimumOrderValue: 'Minimum order value',
    deliveryTimeDays: 'Standard lead time (days)',
    incoterms: 'Incoterms',
    supplierIban: 'IBAN',
    supplierBic: 'BIC',
    supplierBankName: 'Bank',
  },
  types: {
    COMPANY: 'Company',
    PERSON: 'Individual',
  },
  statuses: {
    ACTIVE: 'Active',
    PROSPECT: 'Prospect',
    BLOCKED: 'Blocked',
    INACTIVE: 'Inactive',
  },
  vatIdStatuses: {
    UNCHECKED: 'Unchecked',
    VALID: 'Valid',
    INVALID: 'Invalid',
    NOT_CHECKABLE: 'Not checkable',
    SERVICE_UNAVAILABLE: 'Service unavailable',
  },
  taxCategories: {
    DOMESTIC: 'Regular (domestic)',
    EU_B2B: 'Intra-community (EU B2B)',
    EU_B2C: 'EU B2C',
    THIRD_COUNTRY: 'Export (third country)',
    REVERSE_CHARGE_DOMESTIC: 'Reverse charge (domestic)',
    TAX_EXEMPT: 'Tax exempt',
  },
  industries: {
    RETAIL: 'Retail',
    WHOLESALE: 'Wholesale',
    MANUFACTURING: 'Manufacturing',
    GASTRONOMY: 'Gastronomy',
    LOGISTICS: 'Logistics',
    HEALTHCARE: 'Healthcare',
    PUBLIC_SECTOR: 'Public sector',
    CONSTRUCTION: 'Construction',
    AGRICULTURE: 'Agriculture',
    SERVICES: 'Services',
    OTHER: 'Other',
  },
  leadStatuses: {
    NEW: 'New',
    CONTACTED: 'Contacted',
    QUALIFIED: 'Qualified',
    PROPOSAL_SENT: 'Proposal sent',
    NEGOTIATION: 'Negotiation',
    WON: 'Won',
    LOST: 'Lost',
  },
  leadSources: {
    WEBSITE: 'Website',
    TRADE_FAIR: 'Trade fair',
    REFERRAL: 'Referral',
    COLD_OUTREACH: 'Cold outreach',
    INBOUND_CALL: 'Inbound call',
    OTHER: 'Other',
  },
  supplierRatings: {
    A: 'A – very reliable',
    B: 'B – acceptable',
    C: 'C – critical',
  },
  riskStatuses: {
    NORMAL: 'Unremarkable',
    WATCHLIST: 'Watchlist',
    DELIVERY_LOCK: 'Delivery lock',
    SALES_LOCK: 'Sales lock',
    COLLECTION: 'Debt collection / locked',
    INSOLVENCY: 'Insolvency',
  },
  risk: {
    lockAction: 'Lock customer',
    unlockAction: 'Unlock customer',
    lockTitle: 'Lock business partner',
    lockDescription:
      'The lock immediately prevents every document for {{name}}. The reason is recorded in the audit log.',
    lockConfirm: 'Apply lock',
    lockSuccess: 'The business partner is locked.',
    lockError: 'The lock could not be applied.',
    unlockTitle: 'Release lock',
    unlockDescription: 'The lock on {{name}} will be released. Previous reason: {{reason}}',
    unlockConfirm: 'Release lock',
    unlockSuccess: 'The lock has been released.',
    unlockError: 'The lock could not be released.',
    reasonRequired: 'A reason is required.',
    fields: {
      riskStatus: 'Risk classification',
      followUpStatus: 'Classification after release',
      blockReason: 'Reason for the lock',
      blockReasonHint:
        'Example: “Insolvency court file number: IN 412/26” or “Handed to Creditreform collection on 15/08/2026”.',
      unblockReason: 'Reason for the release',
      unblockReasonHint: 'Example: “Receivable settled in full on 01/09/2026”.',
    },
    banner: {
      blockedTitle: '🚨 WARNING: CUSTOMER IS LOCKED (status: {{status}})',
      warningTitle: 'Risk classification: {{status}}',
      reason: 'Reason: {{reason}}',
      since: 'Locked on {{date}} by {{user}}',
      consequence:
        'No deliveries or invoices on account may be created — release only against prepayment or cash.',
    },
  },
  insurance: {
    policySection: 'Insurance policy',
    claimSection: 'Claim',
    saved: 'The trade credit insurance has been saved.',
    saveError: 'The trade credit insurance could not be saved.',
    limitExceededHint:
      'The insured maximum limit is exhausted. Further deliveries are not covered.',
    claimIdleHint:
      'The claim section becomes relevant once the customer moves to collection or insolvency.',
    openAmount: 'Open receivables: {{amount}}',
    expectedPayout: 'Expected insurance indemnity',
    expectedPayoutHint: 'Claim amount less the deductible of {{percent}}.',
    actions: {
      prepareClaim: 'Record claim',
    },
    statuses: {
      NOT_INSURED: 'Not insured',
      INSURED: 'Insured',
      LIMIT_EXCEEDED: 'Limit exceeded',
      CLAIM_REPORTED: 'Claim reported',
      INDEMNIFIED: 'Indemnified',
    },
    fields: {
      status: 'Insurance status',
      company: 'Insurance company',
      companyHint: 'Example: Allianz Trade / Euler Hermes, Atradius, Coface.',
      policyNumber: 'Policy / limit number',
      limit: 'Insured maximum limit (€)',
      deductiblePercent: 'Deductible (%)',
      deductibleHint: 'Share the tenant carries in case of a claim.',
      claimDate: 'Claim report date',
      claimAmount: 'Reported claim amount (€)',
      payoutAmount: 'Indemnified amount (€)',
    },
  },
  incoterms: {
    EXW: 'Ex works',
    FCA: 'Free carrier',
    FAS: 'Free alongside ship',
    FOB: 'Free on board',
    CFR: 'Cost and freight',
    CIF: 'Cost, insurance and freight',
    CPT: 'Carriage paid to',
    CIP: 'Carriage and insurance paid to',
    DAP: 'Delivered at place',
    DPU: 'Delivered at place unloaded',
    DDP: 'Delivered duty paid',
  },
  documentTypes: {
    QUOTE: 'Quote',
    ORDER: 'Order',
    DELIVERY_NOTE: 'Delivery note',
    INVOICE: 'Invoice',
    CREDIT_NOTE: 'Credit note',
    CANCELLATION: 'Cancellation',
    PURCHASE_ORDER: 'Purchase order',
  },
  documentStatuses: {
    DRAFT: 'Draft',
    SENT: 'Sent',
    ACCEPTED: 'Accepted',
    REJECTED: 'Rejected',
    CONFIRMED: 'Confirmed',
    IN_PROGRESS: 'In progress',
    PARTIALLY_PROCESSED: 'Partially processed',
    SHIPPED: 'Shipped',
    COMPLETED: 'Completed',
    POSTED: 'Posted',
    CANCELLED: 'Cancelled',
  },
  filters: {
    status: 'Status',
    industry: 'Industry',
    industryCode: 'Filter industry code (exact)',
    tags: 'Tags',
    vatIdStatus: 'VAT ID',
    riskStatus: 'Risk',
    leadStatus: 'Pipeline stage',
    leadSource: 'Source',
    supplierRating: 'Rating',
    reset: 'Reset filters',
  },
  states: {
    empty: 'No business partners yet.',
    emptyFiltered: 'No business partners match the current selection.',
    error: 'The list could not be loaded.',
    detailError: 'The business partner could not be loaded.',
  },
  export: {
    csv: 'Download CSV',
    xlsx: 'Download Excel',
    success: 'Export created.',
    error: 'The export failed.',
  },
  bulkDelete: {
    selected: 'Selected: {{count}}',
    action: 'Delete selection',
    help: 'Help with deleting multiple prospects',
    deleted: 'Deleted: {{count}}.',
    failed: 'Not deleted: {{count}}. These entries remain selected.',
    hasDocuments: 'This business partner has documents and cannot be deleted.',
    error: 'The entry could not be deleted.',
    title: 'Delete selected prospects',
    description:
      'Selected prospects will be made inactive and hidden. Partners with documents are preserved. Deletion affects the entire business partner, including any additional roles.',
    select: 'Select {{name}}',
    selectPage: 'Select all on this page',
  },
  delete: {
    title: 'Delete business partner',
    description: 'The partner "{{name}}" will be set to inactive and hidden.',
    success: 'Business partner deleted.',
    error: 'The business partner could not be deleted.',
  },
  pipeline: {
    title: 'Sales pipeline',
    viewSwitch: 'Switch view',
    listView: 'List',
    boardView: 'Board',
    emptyColumn: 'No entries',
    moved: '{{name}} moved to "{{stage}}".',
    moveError: 'The pipeline stage could not be changed.',
    movePrevious: 'Move {{name}} one stage back',
    moveNext: 'Move {{name}} one stage forward',
    lostTitle: 'Mark prospect as lost',
    lostDescription: 'Please provide the reason for the loss.',
    lostPlaceholder: 'e.g. price too high, competitor chosen, project postponed',
  },
  convert: {
    title: 'Convert prospect into customer',
    description: 'The record "{{name}}" is kept; calls, notes, quotes and chats are carried over.',
    debtorHint: 'Leave empty to assign the next free DATEV debtor number.',
    debtorPlaceholder: 'automatic',
    keepProspectRole: 'Keep the "prospect" role as well',
    success: 'Conversion completed. Debtor account {{number}}.',
    error: 'The conversion failed.',
  },
  sales: {
    title: 'Revenue & payment behaviour',
    loadError: 'The metrics could not be loaded.',
    revenueYtd: 'Revenue year to date',
    revenuePreviousYear: 'Revenue previous year',
    revenueChange: 'Change from previous year',
    orderIntake: 'Order intake',
    documentCounts: 'Quotes / orders / delivery notes / invoices',
    averageOrderValue: 'Average order value',
    paidAmount: 'Paid amount',
    credits: 'Credits and cancellations',
    lastDocuments: 'Last order / last invoice',
    period: 'Period',
    periods: {
      currentYear: 'Current year',
      previousYear: 'Previous year',
      last12Months: 'Last 12 months',
      all: 'All time',
      custom: 'Custom range',
    },
    from: 'From',
    to: 'To',
    searchDocuments: 'Search document number or reference …',
    allTypes: 'All document types',
    allStatuses: 'All statuses',
    allPayments: 'All payment statuses',
    noDocumentPermission: 'Document permission is required to view revenue and documents.',
    columns: {
      number: 'Number',
      type: 'Type',
      date: 'Date',
      status: 'Status',
      payment: 'Payment',
      net: 'Net',
      gross: 'Gross',
      open: 'Open',
      due: 'Due',
      salesRep: 'Sales rep',
    },
    revenueTotal: 'Revenue total',
    openInvoices_one: 'Open invoice ({{count}})',
    openInvoices_other: 'Open invoices ({{count}})',
    overdueInvoices_one: 'Overdue invoice ({{count}})',
    overdueInvoices_other: 'Overdue invoices ({{count}})',
    openQuotes: 'Open quotes',
    openOrders: 'Open orders',
    averagePaymentTermsDays: 'Avg. payment terms',
    lastOrderDate: 'Last order',
    documents: 'Documents',
    documentsEmpty: 'No documents yet.',
    documentsError: 'The documents could not be loaded.',
  },
  supplierProducts: {
    title: 'Purchasing conditions',
    subtitle: 'Items, supplier SKUs, purchase prices and lead times.',
    add: 'Link item',
    createTitle: 'Add purchasing condition',
    editTitle: 'Edit purchasing condition',
    description: 'Conditions apply to this supplier and the selected item.',
    product: 'Item',
    productHint: 'Search by article number or name.',
    productPlaceholder: 'Search items …',
    noProducts: 'No items found.',
    supplierSku: 'Supplier SKU',
    purchasePrice: 'Purchase price (net)',
    packagingUnit: 'Packaging unit',
    packagingFactor: 'Base units per packaging unit',
    leadTimeDays: 'Lead time (days)',
    minOrderQuantity: 'Minimum order quantity',
    isPreferredSupplier: 'Preferred supplier for this item',
    preferred: 'Preferred',
    notes: 'Note',
    empty: 'No purchasing conditions yet.',
    saved: 'Purchasing condition saved.',
    saveError: 'The purchasing condition could not be saved.',
    loadError: 'The purchasing conditions could not be loaded.',
    edit: 'Edit purchasing condition',
    delete: 'Delete purchasing condition',
    deleted: 'Purchasing condition deleted.',
    deleteTitle: 'Delete purchasing condition',
    deleteDescription: 'The conditions for "{{name}}" will be removed.',
    columns: {
      article: 'Item',
      supplierSku: 'Supplier SKU',
      purchasePrice: 'Purchase price',
      packaging: 'Packaging',
      leadTimeDays: 'Lead time',
      minOrderQuantity: 'Min. quantity',
      preferred: 'Preferred',
    },
  },
  supplierOrders: {
    purchaseOrder: 'Purchase order',
    itemCount_one: '{{count}} line',
    itemCount_other: '{{count}} lines',
    deliveryDate: 'Goods receipt {{date}}',
    empty: 'No purchase orders for this supplier yet.',
    loadError: 'The purchase orders could not be loaded.',
  },
  addresses: {
    title: 'Alternative addresses',
    add: 'Address',
    isDefault: 'Default',
    empty: 'No alternative addresses stored.',
    deleted: 'Address deleted.',
    delete: 'Delete address',
    deleteTitle: 'Delete address',
    deleteDescription: 'The address will be removed from the partner record.',
    loadError: 'The addresses could not be loaded.',
    types: {
      BILLING: 'Billing address',
      SHIPPING: 'Shipping address',
      OTHER: 'Other',
    },
  },
  contacts: {
    title: 'Contacts',
    add: 'Contact',
    isPrimary: 'Primary contact',
    empty: 'No contacts stored.',
    deleted: 'Contact deleted.',
    delete: 'Delete contact',
    deleteTitle: 'Delete contact',
    deleteDescription: 'The contact will be removed from the partner record.',
    loadError: 'The contacts could not be loaded.',
  },
  customFields: {
    title: 'Dynamic custom fields',
    subtitle: 'Only fields matching this partner’s roles are shown.',
    empty: 'No custom fields defined for the roles of this partner.',
    saved: 'Custom fields saved.',
  },
};
